import { LoggingService } from 'src/app/services/logging.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { APIService, DomainValue } from 'src/app/services/api.service';
import { Program } from '../models/program.model';
import { DataService } from '../../offline/data-service';
import { StateService } from 'src/app/services/state.service';

export interface ProgramMapsAndIds {
  _id: string;
  name: string;
  name_es: string;
  name_fr: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProgramService {
  constructor(
    private readonly apiService: APIService,
    private readonly dataService: DataService,
    private readonly logger: LoggingService,
    private readonly stateService: StateService,
  ) {}

  public loadProgram(id: string): Observable<Program> {
    this.loadingSubject.next(true);

    return this.loadProgramItem(id).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  public loadPrograms(ids: Array<string>): Observable<Array<Program>> {
    this.loadingSubject.next(true);

    return this.loadProgramItems(ids).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  public saveProgram(program: Program): Observable<Program> {
    this.savingSubject.next(true);

    return this.saveProgramItem(program).pipe(
      finalize(() => {
        this.savingSubject.next(false);
      }),
    );
  }

  public deleteProgram(program: Program): Observable<boolean> {
    this.deletingSubject.next(true);

    return this.deleteProgramItem(program._id).pipe(
      map(() => {
        return true;
      }),
      finalize(() => {
        this.deletingSubject.next(false);
      }),
    );
  }

  public getCountries(): Observable<DomainValue[]> {
    return from(this.stateService.getDomainValue('countries'));
  }

  private loadProgramItem(id: string): Observable<Program> {
    if (this.apiService.onLine) {
      return from(this.apiService.getItem(this.collectionName, id));
    } else {
      return from(this.dataService.getProgram(id));
    }
  }

  private loadProgramItems(ids: Array<string>): Observable<Array<Program>> {
    const filters = [{ field: '_id', operator: 'in', value: ids }];
    return from(this.apiService.getCollection({ collection: this.collectionName, filters }));
  }

  private saveProgramItem(program: Program): Observable<Program> {
    if (!program._id) {
      delete program._id;
      return from(this.apiService.createCollectionItem(this.collectionName, program));
    } else {
      return from(this.apiService.saveCollectionItem(this.collectionName, program));
    }
  }

  private deleteProgramItem(id: string | undefined): Observable<void> {
    return from(this.apiService.deleteCollectionItem(this.collectionName, id ?? ''));
  }

  private readonly collectionName = 'programs';
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly savingSubject = new BehaviorSubject<boolean>(false);
  private readonly deletingSubject = new BehaviorSubject<boolean>(false);

  public loading$ = this.loadingSubject.asObservable();
  public saving$ = this.savingSubject.asObservable();
  public deleting$ = this.deletingSubject.asObservable();
}
